import { useRef, useState, type FormEvent, type RefObject } from 'react';
import { KeyRound, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PeriscopeLogo } from '@/components/brand/PeriscopeLogo';
import { SonarScope, type SonarScopeHandle } from '@/components/login/SonarScope';
import { TurnstileWidget, type TurnstileWidgetHandle } from '@/components/login/TurnstileWidget';
import { useAuth } from '@/hooks/useAuth';
import { apiConfig, turnstileConfig } from '@/config/authConfig';
import { setLocalToken, decodeLocalToken } from '@/auth/localSession';
import { seedAuthMe, type AuthMeResponse } from '@/auth/authMeStore';
import type { UserPermissions } from '@ip/shared-types';
import { t } from '@/i18n';

const MIN_PASSWORD_LENGTH = 10;

interface LocalLoginResponse {
  token: string;
  mustChangePassword: boolean;
  user: AuthMeResponse['user'];
  permissions: UserPermissions;
}

type ScopeRef = RefObject<SonarScopeHandle | null>;

/** Activate the local session: seed /auth/me data and store the JWT. */
function activateLocalSession(result: LocalLoginResponse): void {
  const sub = decodeLocalToken(result.token)?.sub;
  if (sub && result.user && result.permissions) {
    seedAuthMe(`local:${sub}`, { user: result.user, permissions: result.permissions });
  }
  setLocalToken(result.token);
}

/** Surfacing exit on the sonar scene, then activate; direct when no scene. */
function surfaceAndActivate(scope: ScopeRef, result: LocalLoginResponse): void {
  if (scope.current) {
    scope.current.surface(() => activateLocalSession(result));
  } else {
    activateLocalSession(result);
  }
}

function LocalLoginForm({
  scope,
  onMustChangePassword,
}: {
  scope: ScopeRef;
  onMustChangePassword: (login: LocalLoginResponse) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const turnstileEnabled = Boolean(turnstileConfig.siteKey);

  // Turnstile tokens are single-use — a fresh one is needed for every attempt.
  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    setTurnstileToken(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (turnstileEnabled && !turnstileToken) {
      toast.error(t('auth.turnstileRequired'));
      return;
    }
    setSubmitting(true);
    scope.current?.submitPing();
    // Stays true through the surfacing exit — re-enabling the button while
    // the scene surfaces would open a double-submit window.
    let succeeded = false;
    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/local/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          turnstileToken ? { email, password, turnstileToken } : { email, password },
        ),
      });

      if (response.status === 429) {
        scope.current?.errorEcho();
        toast.error(t('auth.tooManyAttempts'));
        resetTurnstile();
        return;
      }
      if (response.status === 403) {
        scope.current?.errorEcho();
        toast.error(t('auth.turnstileFailed'));
        resetTurnstile();
        return;
      }
      if (response.status === 503) {
        // Turnstile siteverify outage (backend fails closed) or local auth
        // not configured — NOT a credentials problem.
        scope.current?.errorEcho();
        toast.error(t('auth.verificationUnavailable'));
        resetTurnstile();
        return;
      }
      if (!response.ok) {
        // Generic message regardless of reason (no user enumeration)
        scope.current?.errorEcho();
        toast.error(t('auth.invalidCredentials'));
        resetTurnstile();
        return;
      }

      const result = (await response.json()) as LocalLoginResponse;
      if (result.mustChangePassword) {
        onMustChangePassword(result);
      } else {
        succeeded = true;
        surfaceAndActivate(scope, result);
      }
    } catch {
      scope.current?.errorEcho();
      toast.error(t('auth.loginFailed'));
      resetTurnstile();
    } finally {
      if (!succeeded) setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email">{t('auth.email')}</Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password">{t('auth.password')}</Label>
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {turnstileEnabled && (
        <TurnstileWidget
          ref={turnstileRef}
          siteKey={turnstileConfig.siteKey}
          onToken={setTurnstileToken}
        />
      )}
      <Button size="lg" type="submit" className="w-full" disabled={submitting}>
        {submitting ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />}
        {t('auth.signIn')}
      </Button>
    </form>
  );
}

function ChangePasswordForm({ scope, login }: { scope: ScopeRef; login: LocalLoginResponse }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.error(t('auth.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    scope.current?.submitPing();
    let succeeded = false;
    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/local/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${login.token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        scope.current?.errorEcho();
        toast.error(
          response.status === 401 || response.status === 403
            ? t('auth.wrongCurrentPassword')
            : t('auth.changePasswordFailed'),
        );
        return;
      }

      // Password changed — enter the app with the session from login.
      succeeded = true;
      surfaceAndActivate(scope, login);
    } catch {
      scope.current?.errorEcho();
      toast.error(t('auth.changePasswordFailed'));
    } finally {
      if (!succeeded) setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-center">
        <h2 className="text-base font-semibold">{t('auth.changePasswordTitle')}</h2>
        <p className="text-muted-foreground mt-1 text-xs">{t('auth.changePasswordSubtitle')}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="current-password">{t('auth.currentPassword')}</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <p className="text-muted-foreground text-xs">
            {t('auth.passwordMinLengthHint', { min: MIN_PASSWORD_LENGTH })}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        <Button size="lg" type="submit" className="w-full" disabled={submitting}>
          {submitting ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
          {t('auth.changePasswordSubmit')}
        </Button>
      </form>
    </div>
  );
}

export function LoginPage() {
  const { login, isLoading } = useAuth();
  const scopeRef = useRef<SonarScopeHandle>(null);
  // When set, the user authenticated with a temporary password and MUST
  // change it before entering the app.
  const [pendingPasswordChange, setPendingPasswordChange] = useState<LocalLoginResponse | null>(null);

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#0b0a1f]"
        role="status"
        aria-label="Loading"
      >
        <Loader2 className="size-8 animate-spin text-indigo-300/60" />
      </div>
    );
  }

  const handleMicrosoftLogin = () => {
    scopeRef.current?.submitPing();
    login();
  };

  return (
    <SonarScope ref={scopeRef}>
      <div className="w-full rounded-2xl border border-indigo-300/15 bg-zinc-950/70 p-8 shadow-2xl shadow-indigo-950/60 backdrop-blur-md">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-16 items-center justify-center rounded-2xl bg-indigo-500/10 text-indigo-300 ring-1 ring-indigo-300/20">
            <PeriscopeLogo data-scope-logo className="size-8" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-100">
              {t('app.name')}
            </h1>
            <p data-scope-tagline className="mt-1 text-sm text-zinc-400">
              {t('app.tagline')}
            </p>
          </div>
        </div>

        <div className="mt-8">
          {pendingPasswordChange ? (
            <ChangePasswordForm scope={scopeRef} login={pendingPasswordChange} />
          ) : (
            <Tabs defaultValue="microsoft">
              <TabsList className="w-full">
                <TabsTrigger value="microsoft" className="flex-1">
                  {t('auth.tabMicrosoft')}
                </TabsTrigger>
                <TabsTrigger value="password" className="flex-1">
                  {t('auth.tabPassword')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="microsoft" className="mt-4 space-y-4">
                <Button size="lg" className="w-full" onClick={handleMicrosoftLogin}>
                  <LogIn className="size-4" />
                  {t('auth.signInWithMicrosoft')}
                </Button>
                <p className="text-muted-foreground text-center text-xs">
                  {t('auth.microsoftHint')}
                </p>
              </TabsContent>

              <TabsContent value="password" className="mt-4">
                <LocalLoginForm scope={scopeRef} onMustChangePassword={setPendingPasswordChange} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </SonarScope>
  );
}
